<template>
	<div id="clientDns" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Menus">
			<button class="hu-pill" @click="createNewMenu()" title="Create a new menu">
				<font-awesome-icon :icon="['fas', 'plus']" />
				<span>New Menu</span>
			</button>
		</JuniperPageHeader>
		<div class="row mb-3">
			<!-- Menu picker -->
			<div class="col-md-12 mt-3">
				<div class="jm-toolbar">
					<label class="jm-toolbar__label">Menu</label>
					<select @change="selectMenu(selectedMenuFromList)" v-model="selectedMenuFromList" class="jm-select">
						<option value="">Select a menu to edit…</option>
						<option v-for="(menu, i) in menus" :key="menu.id" :value="i">{{ menu.name }} ({{ printLocations(menu.display_location) }})</option>
					</select>
					<span class="jm-toolbar__or">or</span>
					<button type="button" class="hu-pill hu-pill--accent" @click="createNewMenu()">
						<font-awesome-icon :icon="['fas', 'plus']" />
						<span>New Menu</span>
					</button>
				</div>
			</div>

			<!-- Choose Menu Type -->
			<div class="col-md-12 mt-3" v-if="selectedMenu">
				<div class="jm-panel">
					<div class="jm-panel__row">
						<span class="jm-panel__label">Menu type</span>
						<div class="jm-seg">
							<button type="button" class="jm-seg__btn" :class="{ active: selectedMenu.type === 'default' }" @click="selectedMenu.type = 'default'">Default</button>
							<button type="button" class="jm-seg__btn" :class="{ active: selectedMenu.type === 'mega-menu' }" @click="selectedMenu.type = 'mega-menu'">Mega Menu</button>
							<button
								type="button"
								v-if="client.supported_menus && client.supported_menus.includes('accordion')"
								class="jm-seg__btn"
								:class="{ active: selectedMenu.type === 'accordion-menu' }"
								@click="selectedMenu.type = 'accordion-menu'"
							>
								Accordion
							</button>
						</div>
					</div>
					<p v-if="selectedMenu.type == 'default'" class="jm-panel__hint">
						<font-awesome-icon class="me-1" :icon="['fas', 'info-circle']" />
						Default menu type can be used for any menu the theme supports (primary, hero, news, social…).
					</p>
					<p v-else-if="selectedMenu.type == 'mega-menu'" class="jm-panel__hint">
						<font-awesome-icon class="me-1" :icon="['fas', 'info-circle']" />
						<span><strong>Mega menu</strong> can only be used for the Primary Menu.</span>
					</p>
					<p v-else-if="selectedMenu.type == 'accordion-menu'" class="jm-panel__hint jm-panel__hint--warn">
						<font-awesome-icon class="me-1" :icon="['fas', 'exclamation-triangle']" />
						<span><strong>Accordion menu</strong> can only be used for the departments Sidebar Menu.</span>
					</p>
				</div>
			</div>

			<!-- Available Menu Items -->
			<div class="col-md-4 col-lg-4 mt-4" v-if="selectedMenu && (selectedMenu.type == 'default' || selectedMenu.type == 'accordion-menu')">
				<div class="accordion jm-items-panel" id="accordionExample">
					<!-- Pages for menu -->
					<div class="accordion-item pages-item">
						<h2 class="accordion-header" id="headingPages">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#collapsePages"
								aria-expanded="false"
								aria-controls="collapsePages"
							>
								Pages
							</button>
						</h2>
						<div id="collapsePages" class="accordion-collapse collapse" aria-labelledby="headingPages" data-bs-parent="#accordionExample">
							<div class="accordion-body" style="max-height:205px;overflow:auto">
								<div class="form-group mb-2">
									<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchPageItems()" v-model="pagesSearch" />
								</div>
								<div class="available-pages">
									<template v-for="page in availableMenuItems.pages">
										<div class="d-flex justify-content-between align-items-center mb-2" :key="page.id + '-page-menu-item'" v-if="!page.hidden">
											<span>{{ page.title }}</span>
											<button @click="addItemToMenu(page, 'page')" class="btn btn-sm btn-outline-primary">
												Add
											</button>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
					<!-- News for menu -->
					<div class="accordion-item news-item">
						<h2 class="accordion-header" id="headingNews">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#collapseNews"
								aria-expanded="false"
								aria-controls="collapseNews"
							>
								News & Notices
							</button>
						</h2>
						<div id="collapseNews" class="accordion-collapse collapse" aria-labelledby="headingNews" data-bs-parent="#accordionExample">
							<div class="accordion-body" style="max-height:205px;overflow:auto">
								<div class="form-group mb-2">
									<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchNewsItems()" v-model="newsSearch" />
								</div>
								<div class="available-news">
									<template v-for="newsItem in availableMenuItems.news">
										<div class="d-flex justify-content-between align-items-center mb-2" :key="newsItem.id + '-news-menu-item'" v-if="!newsItem.hidden">
											<span>{{ newsItem.title }}</span>
											<button @click="addItemToMenu(newsItem, 'news')" class="btn btn-sm btn-outline-primary">
												Add
											</button>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
					<!-- Posts for menu -->
					<div class="accordion-item posts-item">
						<h2 class="accordion-header" id="headingPosts">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#collapsePosts"
								aria-expanded="false"
								aria-controls="collapsePosts"
							>
								Posts
							</button>
						</h2>
						<div id="collapsePosts" class="accordion-collapse collapse" aria-labelledby="headingPosts" data-bs-parent="#accordionExample">
							<div class="accordion-body"></div>
						</div>
					</div>
					<!-- Departments for menu -->
					<div class="accordion-item departments-item">
						<h2 class="accordion-header" id="headingDepartments">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#collapseDepartments"
								aria-expanded="false"
								aria-controls="collapseDepartments"
							>
								Departments
							</button>
						</h2>
						<div id="collapseDepartments" class="accordion-collapse collapse" aria-labelledby="headingDepartments" data-bs-parent="#accordionExample">
							<div class="accordion-body" style="max-height:205px;overflow:auto">
								<div class="form-group mb-2">
									<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchDepartmentsItems()" v-model="departmentsSearch" />
								</div>
								<div class="available-departments">
									<template v-for="departmentItem in availableMenuItems.departments">
										<div
											class="d-flex justify-content-between align-items-center mb-2"
											:key="departmentItem.id + '-departments-menu-item'"
											v-if="!departmentItem.hidden"
										>
											<span>
												{{ departmentItem.title }}
												<small v-if="departmentItem.type && departmentItem.type !== 'department'" class="text-muted ms-1">/{{ departmentItem.prefix }}</small>
											</span>
											<button @click="addItemToMenu(departmentItem, 'department')" class="btn btn-sm btn-outline-primary">
												Add
											</button>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
					<!-- Predefined for menu -->
					<div class="accordion-item predefined-item">
						<h2 class="accordion-header" id="headingPredefined">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#collapsePredefined"
								aria-expanded="false"
								aria-controls="collapsePredefined"
							>
								Predefined
							</button>
						</h2>
						<div id="collapsePredefined" class="accordion-collapse collapse" aria-labelledby="headingPredefined" data-bs-parent="#accordionExample">
							<div class="accordion-body">
								<div class="available-predefined">
									<template v-for="(pItem, index) in availableMenuItems.predefined">
										<div :key="index + '-page-menu-item'" class="d-flex justify-content-between align-items-center mb-2">
											<span>{{ pItem.title }}</span>
											<button @click="addItemToMenu(pItem, 'predefined')" class="btn btn-sm btn-outline-primary">
												Add
											</button>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
					<!-- Custom URLs for menu -->
					<div class="accordion-item custom-item">
						<h2 class="accordion-header" id="headingCustom">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#collapseCustom"
								aria-expanded="false"
								aria-controls="collapseCustom"
							>
								Custom URL
							</button>
						</h2>
						<div id="collapseCustom" class="accordion-collapse collapse" aria-labelledby="headingCustom" data-bs-parent="#accordionExample">
							<div class="accordion-body">
								<div class="form-group mb-2">
									<label for="">URL</label>
									<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="customUrl.url" />
								</div>
								<div class="form-group mb-2">
									<label for="">Navigation Label</label>
									<input type="text" class="form-control form-control-sm" v-model="customUrl.label" />
								</div>
								<button
									:disabled="customUrl.url == '' || customUrl.label == ''"
									class="btn btn-primary btn-sm float-end mb-3"
									@click="addItemToMenu(customUrl, 'custom')"
								>
									Add
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Default Menu Structure -->
			<div class="col-md-8 col-lg-8 mt-4" v-if="selectedMenu && (selectedMenu.type == 'default' || selectedMenu.type == 'accordion-menu')">
				<div class="card jm-default">
					<div class="card-body">
						<!-- Default Menu Options -->
						<div class="row">
							<div class="col-md-6">
								<div class="form-group">
									<h6 class="">Name</h6>
									<input class="form-control" type="text" v-model="selectedMenu.name" />
								</div>
							</div>
						</div>
						<!-- Menu Items -->
						<div class="menu-items-list" style="width:500px">
							<div class="d-flex justify-content-between align-items-center">
								<div class="d-flex justify-content-start align-items-center mt-4" style="min-height:30px">
									<h6 class="mb-0">Items</h6>
									<div class="form-check select-item-checkbox">
										<input class="form-check-input fs-6" @click="selectAllMenuItems" v-model="allMenuItemsSelected" type="checkbox" />
									</div>
								</div>

								<div class="actions d-flex align-items-center mt-4" style="min-height:30px">
									<button @click="deleteSelectedItems" v-if="selectedMenuItems.length > 0" class="jm-del">
										<font-awesome-icon :icon="['fas', 'trash-alt']" />
										Delete {{ selectedMenuItems.length }} item{{ selectedMenuItems.length === 1 ? '' : 's' }}
									</button>
								</div>
							</div>

							<div v-if="selectedMenu.items.length > 0" class="accordion" id="accordionMenu">
								<Tree :value="selectedMenu.items" triggerClass="drag-move">
									<div class="accordion-item card" slot-scope="{ node, index, tree, path }">
										<h2 class="accordion-header d-flex justify-content-center align-items-center drag-move" :id="'heading' + node.timestamp">
											<div class="form-check select-item-checkbox">
												<input class="form-check-input fs-6" @click="selectMenuItem" v-model="selectedMenuItems" :value="node.timestamp" type="checkbox" />
											</div>
											<button
												type="button"
												v-if="node.children && node.children.length > 0"
												class="jm-fold"
												:class="{ 'jm-fold--open': !node.$folded }"
												@click="tree.toggleFold(node, path)"
												:title="node.$folded ? 'Expand children' : 'Collapse children'"
											>
												<font-awesome-icon :icon="['fas', 'chevron-right']" />
											</button>
											<button
												class="accordion-button collapsed p-2"
												type="button"
												data-bs-toggle="collapse"
												:data-bs-target="'#collapse' + node.timestamp"
												aria-expanded="false"
												:aria-controls="'collapse' + node.timestamp"
											>
												{{ node.label }} -
												<small class="text-primary fw-bold ms-1">{{ node.type }}</small>
											</button>
										</h2>
										<div
											:id="'collapse' + node.timestamp"
											class="accordion-collapse collapse"
											:aria-labelledby="'heading' + node.timestamp"
											data-bs-parent="#accordionMenu"
										>
											<div class="accordion-body">
												<div class="form-group mb-2" v-if="node.type == 'custom'">
													<label for="">URL</label>
													<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="node.url" />
												</div>
												<div v-else class="form-group mb-2">Original: {{ node.title }}</div>
												<div class="form-group mb-2">
													<label for="">Navigation Label</label>
													<input type="text" class="form-control form-control-sm" v-model="node.label" />
												</div>
												<div class="form-group mb-2">
													<label for="">Description</label>
													<input type="text" class="form-control form-control-sm" v-model="node.description" />
												</div>
												<div class="form-group mb-2">
													<label for="">Icon </label>

													<input type="text" class="form-control form-control-sm" v-model="node.icon" />
													<small
														>Example: <code>fa-solid fa-house</code>
														<a class="ms-2" target="_blank" href="https://fontawesome.com/search?o=r&m=free">available icons</a></small
													>
												</div>

												<div class="form-group mb-2">
													<label for="">Class </label>
													<input type="text" class="form-control form-control-sm" v-model="node.class" />
													<small><code>Additional class for menu item</code></small>
												</div>

												<div class="row mb-2">
													<div class="col-md-4">
														<!-- Disable link -->
														<div class="form-check mb-2">
															<input class="form-check-input" type="checkbox" v-model="node.disabled" :id="'disableLink-' + index" />
															<label class="form-check-label" :for="'disableLink-' + index">
																Disable Link
															</label>
														</div>
													</div>
													<div class="col-md-4">
														<!-- New tab -->
														<div class="form-check mb-2">
															<input class="form-check-input" type="checkbox" v-model="node.newTab" :id="'newTab-' + index" />
															<label class="form-check-label" :for="'newTab-' + index">
																New Tab
															</label>
														</div>
													</div>
													<div class="col-md-4">
														<!-- No label -->
														<div class="form-check mb-2">
															<input class="form-check-input" type="checkbox" v-model="node.noLabel" :id="'noLabel-' + index" />
															<label class="form-check-label" :for="'noLabel-' + index">
																Disable Label
															</label>
														</div>
													</div>
												</div>

												<div class="row mb-2">
													<div class="col-md-6">
														<!-- Rel -->
														<div class="form-group">
															<label :for="'rel' + index">Rel</label>
															<select class="form-select">
																<option value="">none</option>
																<option v-for="(relItem, i) in relOptions" :value="relItem" :key="i + 'rel'">{{ relItem }}</option>
															</select>
														</div>
													</div>
												</div>

												<div class="row">
													<div class="col-md-12 justify-content-between align-items-center">
														<button
															v-if="!replaceSectionShow"
															class="btn btn-sm btn-outline-info float-start mb-3 mt-2"
															@click="selectItemToReplace(node)"
														>
															Replace
														</button>
														<button class="btn btn-sm btn-outline-danger float-end mb-3 mt-2" @click="removeMenuItem(node.timestamp)">
															Remove
														</button>
													</div>
													<div v-if="replaceSectionShow" class="col-md-12 replace-section card pt-3">
														<div class="row">
															<div class="col-md-12 mb-3">
																<input
																	type="text"
																	placeholder="search..."
																	v-model="replacesSearch"
																	@input="searchReplaceItems()"
																	class="form-control mb-3"
																/>
																<div style="height:200px;overflow:auto">
																	<template v-for="(rep, i) in replacingItems">
																		<div :key="i" v-if="!rep.hidden" class="d-flex justify-content-between align-items-center mb-1">
																			<span>{{ rep.title }}</span>
																			<div class="d-flex justify-content-end align-items-center">
																				<span>{{ rep.type }}</span>
																				<button @click="selectReplacingItem(rep)" class="btn btn-sm btn-outline-primary ms-2">
																					Select
																				</button>
																			</div>
																		</div>
																	</template>
																</div>
															</div>
															<div class="col-md-12 d-flex justify-content-between align-items-center mb-2">
																<button @click="replaceSectionShow = false" class="btn btn-secondary btn-sm">
																	Cancel
																</button>
																<span v-if="replacingItem">{{ replacingItem.title }}</span>
																<button :disabled="!replacingItem" @click="replaceMenuItem()" class="btn btn-primary btn-sm">
																	Replace
																</button>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</Tree>
							</div>
							<div v-else>
								<code>No menu items added</code>
							</div>
						</div>

						<div class="row mb-3">
							<div class="col-md-2">
								<h6 class="mt-4">Display Location</h6>
							</div>
							<div class="col-md-9 mt-4">
								<div v-for="dLocation in displayLocations" :key="dLocation.code">
									<div class="form-check mb-2">
										<input class="form-check-input" type="checkbox" v-model="selectedMenu.display_location" :value="dLocation.code" :id="dLocation.code" />
										<label class="form-check-label" :for="dLocation.code">
											{{ dLocation.label }}
										</label>
									</div>
								</div>
							</div>
						</div>
						<button v-if="selectedMenu.id" class="ju-btn ju-btn--danger float-start" @click="deleteMenu()">
							<font-awesome-icon :icon="['fas', 'trash-alt']" />
							Delete
						</button>

						<button v-if="!selectedMenu.id" @click="storeMenu()" class="ju-btn ju-btn--primary float-end">
							<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
							Save
						</button>
						<button v-else @click="updateMenu()" class="ju-btn ju-btn--primary float-end">
							<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
							Update
						</button>
					</div>
				</div>
			</div>

			<!-- Mega Menu Structure -->
			<div class="col-md-12" v-if="selectedMenu && selectedMenu.type == 'mega-menu'">
				<div class="card jm-mega">
					<div class="card-body">
						<div class="jm-note mb-3">
							<font-awesome-icon class="me-1" :icon="['fas', 'info-circle']" />
							<span>This menu is using the <strong>Juniper Mega Menu</strong>.</span>
						</div>
						<!-- Mega Menu Options -->
						<div class="row mb-3">
							<div class="col-md-6">
								<div class="form-group mb-2">
									<h6 class="">Name</h6>
									<input class="form-control" type="text" v-model="selectedMenu.name" />
								</div>
								<div class="form-group mb-2">
									<h6 class="">Menu Width</h6>
									<select v-model="selectedMenu.menu_width" class="form-select" style="width:200px">
										<option value="full">Full Width</option>
										<option value="container">Container</option>
									</select>
								</div>
							</div>
							<div class="col-md-6"></div>
						</div>

						<!-- Mega Menu Items -->
						<div class="mb-4">
							<div v-if="selectedMenu.items.length > 0">
								<draggable v-model="selectedMenu.items" handle=".move-item">
									<div class="megamenu-main-items-container card mb-1" v-for="(node, index) in selectedMenu.items" :key="node.timestamp">
										<div class="megamenu-main-item">
											<div class="left-side">
												<font-awesome-icon class="move-item ms-1 me-2" :icon="['fas', 'bars']" />
												<span>
													{{ node.label }} -
													<small class="text-primary fw-bold ms-1">{{ node.type }}</small>
												</span>
												<button
													@click="
														!settingsSectionShow == index || settingsSectionShow == null ? (settingsSectionShow = index) : (settingsSectionShow = null)
													"
													class="action-button ms-3"
												>
													<font-awesome-icon :icon="['fas', 'cog']" /> config
												</button>
											</div>
											<div class="right-side">
												<span class="jm-stat me-3" :class="{ 'jm-stat--unset': !Array.isArray(node.columns) }">{{ menuItemsMainStatistic(node) }}</span>
												<button @click="openMegaMenuSettings(node)" class="action-button action-button--accent">
													<font-awesome-icon :icon="['fas', 'wrench']" /> Mega Menu
												</button>
											</div>
										</div>
										<!-- Menu Item Settings -->
										<div class="megamenu-main-item-settings">
											<div v-if="settingsSectionShow == index && settingsSectionShow != null" class="menu-item-settings">
												<div class="form-group mb-2" v-if="node.type == 'custom'">
													<label for="">URL</label>
													<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="node.url" />
												</div>
												<div v-else class="form-group mb-2">Original: {{ node.title }}</div>

												<div class="form-group mb-2">
													<label for="">Navigation Label</label>
													<input type="text" class="form-control form-control-sm" v-model="node.label" />
												</div>

												<div class="form-group mb-2">
													<label for="">Description</label>
													<input type="text" class="form-control form-control-sm" v-model="node.description" />
												</div>

												<div class="form-group mb-2">
													<label for="">Icon </label>

													<input type="text" class="form-control form-control-sm" v-model="node.icon" />
													<small
														>Example: <code>fa-solid fa-house</code>
														<a class="ms-2" target="_blank" href="https://fontawesome.com/search?o=r&m=free">available icons</a></small
													>
												</div>

												<div class="form-group mb-2">
													<label for="">Class </label>
													<input type="text" class="form-control form-control-sm" v-model="node.class" />
													<small><code>Additional class for menu item</code></small>
												</div>

												<div class="row mb-2">
													<div class="col-md-4">
														<!-- Disable link -->
														<div class="form-check mb-2">
															<input class="form-check-input" type="checkbox" v-model="node.disabled" :id="'disableLink-' + index" />
															<label class="form-check-label" :for="'disableLink-' + index">
																Disable Link
															</label>
														</div>
													</div>
													<div class="col-md-4">
														<!-- New tab -->
														<div class="form-check mb-2">
															<input class="form-check-input" type="checkbox" v-model="node.newTab" :id="'newTab-' + index" />
															<label class="form-check-label" :for="'newTab-' + index">
																New Tab
															</label>
														</div>
													</div>
													<div class="col-md-4">
														<!-- No label -->
														<div class="form-check mb-2">
															<input class="form-check-input" type="checkbox" v-model="node.noLabel" :id="'noLabel-' + index" />
															<label class="form-check-label" :for="'noLabel-' + index">
																Disable Label
															</label>
														</div>
													</div>
												</div>

												<div class="row mb-2">
													<div class="col-md-6">
														<!-- Rel -->
														<div class="form-group">
															<label :for="'rel' + index">Rel</label>
															<select class="form-select">
																<option value="">none</option>
																<option v-for="(relItem, i) in relOptions" :value="relItem" :key="i + 'rel'">{{ relItem }}</option>
															</select>
														</div>
													</div>
												</div>

												<div class="row">
													<div class="col-md-12 justify-content-between align-items-center">
														<button
															v-if="!replaceSectionShow"
															class="btn btn-sm btn-outline-info float-start mb-3 mt-2"
															@click="selectItemToReplace(node)"
														>
															Replace
														</button>
														<button class="btn btn-sm btn-outline-danger float-end mb-3 mt-2" @click="removeMenuItem(node.timestamp)">
															Remove
														</button>
													</div>
													<div v-if="replaceSectionShow" class="col-md-12 replace-section card pt-3">
														<div class="row">
															<div class="col-md-12 mb-3">
																<input
																	type="text"
																	placeholder="search..."
																	v-model="replacesSearch"
																	@input="searchReplaceItems()"
																	class="form-control mb-3"
																/>
																<div style="height:200px;overflow:auto">
																	<template v-for="(rep, i) in replacingItems">
																		<div :key="i" v-if="!rep.hidden" class="d-flex justify-content-between align-items-center mb-1">
																			<span>{{ rep.title }}</span>
																			<div class="d-flex justify-content-end align-items-center">
																				<span>{{ rep.type }}</span>
																				<button @click="selectReplacingItem(rep)" class="btn btn-sm btn-outline-primary ms-2">
																					Select
																				</button>
																			</div>
																		</div>
																	</template>
																</div>
															</div>
															<div class="col-md-12 d-flex justify-content-between align-items-center mb-2">
																<button @click="replaceSectionShow = false" class="btn btn-secondary btn-sm">
																	Cancel
																</button>
																<span v-if="replacingItem">{{ replacingItem.title }}</span>
																<button :disabled="!replacingItem" @click="replaceMenuItem()" class="btn btn-primary btn-sm">
																	Replace
																</button>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</draggable>
							</div>
						</div>

						<div class="row mb-3">
							<div class="col-md-12">
								<button @click="megamenuAddMainItemFlag = true" class="ju-btn ju-btn--primary">
									<font-awesome-icon :icon="['fas', 'plus']" /> Add Item
								</button>
							</div>
						</div>

						<!--Add New Item Mega Menu Parent -->
						<div v-if="megamenuAddMainItemFlag" class="megamenu-adding-items-main mt-3 p-3 card">
							<h5>Select menu item from groups</h5>
							<small>Note that is always better to use predefined items if you need some autogenerated pages</small>
							<div class="accordion" id="accordionExample">
								<!-- Pages for menu -->
								<div class="accordion-item pages-item">
									<h2 class="accordion-header" id="headingPages">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapsePages"
											aria-expanded="false"
											aria-controls="collapsePages"
										>
											Pages
										</button>
									</h2>
									<div id="collapsePages" class="accordion-collapse collapse" aria-labelledby="headingPages" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white" style="max-height:205px;overflow:auto">
											<div class="form-group mb-2">
												<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchPageItems()" v-model="pagesSearch" />
											</div>
											<div class="available-pages">
												<template v-for="page in availableMenuItems.pages">
													<div class="d-flex justify-content-between align-items-center mb-2" :key="page.id + '-page-menu-item'" v-if="!page.hidden">
														<span>{{ page.title }}</span>
														<button @click="addItemToMenu(page, 'page')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- News for menu -->
								<div class="accordion-item news-item">
									<h2 class="accordion-header" id="headingNews">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseNews"
											aria-expanded="false"
											aria-controls="collapseNews"
										>
											News & Notices
										</button>
									</h2>
									<div id="collapseNews" class="accordion-collapse collapse" aria-labelledby="headingNews" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white" style="max-height:205px;overflow:auto">
											<div class="form-group mb-2">
												<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchNewsItems()" v-model="newsSearch" />
											</div>
											<div class="available-news">
												<template v-for="newsItem in availableMenuItems.news">
													<div
														class="d-flex justify-content-between align-items-center mb-2"
														:key="newsItem.id + '-news-menu-item'"
														v-if="!newsItem.hidden"
													>
														<span>{{ newsItem.title }}</span>
														<button @click="addItemToMenu(newsItem, 'news')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- Departments for menu -->
								<div class="accordion-item departments-item">
									<h2 class="accordion-header" id="headingDepartments">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseDepartments"
											aria-expanded="false"
											aria-controls="collapseDepartments"
										>
											Departments
										</button>
									</h2>
									<div id="collapseDepartments" class="accordion-collapse collapse" aria-labelledby="headingDepartments" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white" style="max-height:205px;overflow:auto">
											<div class="form-group mb-2">
												<input
													type="text"
													class="form-control form-control-sm"
													placeholder="Search..."
													@input="searchDepartmentsItems()"
													v-model="departmentsSearch"
												/>
											</div>
											<div class="available-departments">
												<template v-for="departmentItem in availableMenuItems.departments">
													<div
														class="d-flex justify-content-between align-items-center mb-2"
														:key="departmentItem.id + '-departments-menu-item'"
														v-if="!departmentItem.hidden"
													>
														<span>
															{{ departmentItem.title }}
															<small v-if="departmentItem.type && departmentItem.type !== 'department'" class="text-muted ms-1">/{{ departmentItem.prefix }}</small>
														</span>
														<button @click="addItemToMenu(departmentItem, 'department')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- Predefined for menu -->
								<div class="accordion-item predefined-item">
									<h2 class="accordion-header" id="headingPredefined">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapsePredefined"
											aria-expanded="false"
											aria-controls="collapsePredefined"
										>
											Predefined
										</button>
									</h2>
									<div id="collapsePredefined" class="accordion-collapse collapse" aria-labelledby="headingPredefined" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white">
											<div class="available-predefined">
												<template v-for="(pItem, index) in availableMenuItems.predefined">
													<div :key="index + '-page-menu-item'" class="d-flex justify-content-between align-items-center mb-2">
														<span>{{ pItem.title }}</span>
														<button @click="addItemToMenu(pItem, 'predefined')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- Custom URLs for menu -->
								<div class="accordion-item custom-item">
									<h2 class="accordion-header" id="headingCustom">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseCustom"
											aria-expanded="false"
											aria-controls="collapseCustom"
										>
											Custom URL
										</button>
									</h2>
									<div id="collapseCustom" class="accordion-collapse collapse" aria-labelledby="headingCustom" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white">
											<div class="form-group mb-2">
												<label for="">URL</label>
												<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="customUrl.url" />
											</div>
											<div class="form-group mb-2">
												<label for="">Navigation Label</label>
												<input type="text" class="form-control form-control-sm" v-model="customUrl.label" />
											</div>
											<button
												:disabled="customUrl.url == '' || customUrl.label == ''"
												class="btn btn-primary btn-sm mb-3"
												@click="addItemToMenu(customUrl, 'custom')"
											>
												Add
											</button>
										</div>
									</div>
								</div>
							</div>
							<button class="btn btn-secondary btn-sm mt-3" @click="megamenuAddMainItemFlag = false">
								close
							</button>
						</div>

						<div class="row mb-3">
							<div class="col-md-2">
								<h6 class="mt-4">Display Location</h6>
							</div>
							<div class="col-md-9 mt-4">
								<div v-for="dLocation in displayLocations" :key="dLocation.code">
									<div v-if="dLocation.label.endsWith('PRIMARY')" class="form-check mb-2">
										<input class="form-check-input" type="checkbox" v-model="selectedMenu.display_location" :value="dLocation.code" :id="dLocation.code" />
										<label class="form-check-label" :for="dLocation.code">
											{{ dLocation.label }}
										</label>
									</div>
								</div>
								<small>Mega menu can only be displayed as primary menu of a theme</small>
							</div>
						</div>

						<div class="row">
							<div class="c-l-md-12">
								<button v-if="selectedMenu.id" class="ju-btn ju-btn--danger float-start" @click="deleteMenu()">
									<font-awesome-icon :icon="['fas', 'trash-alt']" />
									Delete
								</button>

								<button v-if="!selectedMenu.id" @click="storeMenu()" class="ju-btn ju-btn--primary float-end">
									<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
									Save
								</button>
								<button v-else @click="updateMenu()" class="ju-btn ju-btn--primary float-end">
									<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
									Update
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- Media Library Modal -->
		<MediaLibraryModal @mediaInsert="handleMediaInsert" ref="mediaLibraryModal" />

		<!-- Modal Mega Menu -->
		<div class="modal fade" id="modalMegaMenu" ref="modalMegaMenu" aria-labelledby="modalMegaMenuLabel">
			<div class="modal-dialog modal-xl">
				<div class="modal-content" v-if="megaMenuSettingsObject">
					<div class="modal-header">
						<h5 class="modal-title" id="modalMegaMenuLabel">Mega Menu Settings: {{ megaMenuSettingsObject.label }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="bd-callout bd-callout-info bg-warning-lighter rounded-1 text-black p-1 mb-2">
							<b>Juniper Mega Menu</b> Things to know about megamenu is that you can move menu items between columns but you can't move their childs in upper level.
							If you change columns of Mega Menu, all items will be added in first column of new created structire and then you need to rearange them again. For
							adding menu items press add item on bottom of column and for adding item child press add item on submenu where you want to add.
						</div>
						<div>
							<div class="d-flex justify-content-start align-items-center mb-2">
								<p class="p-0 m-0 me-2">How many columns you want for this submenu?</p>
								<select @change="confrimColumnsCount()" v-model="megaMenuSettingsObject.columnsCount" class="form-select form-select-sm" style="width:150px">
									<option value="1">1 Column</option>
									<option value="2">2 Columns</option>
									<option value="3">3 Columns</option>
									<option value="4">4 Columns</option>
								</select>
								<button @click="confrimColumnsCount()" class="btn btn-primary btn-sm d-none">
									Confirm
								</button>
							</div>
						</div>
						<div class="mega-menu-columns mt-3" v-if="Array.isArray(megaMenuSettingsObject.columns)">
							<div class="row">
								<div
									:class="[
										{
											'col-md-12': megaMenuSettingsObject.columnsCount == '1',
											'col-md-6': megaMenuSettingsObject.columnsCount == '2',
											'col-md-4': megaMenuSettingsObject.columnsCount == '3',
											'col-md-3': megaMenuSettingsObject.columnsCount == '4' },
									]"
									v-for="column in megaMenuSettingsObject.columns"
									:key="'column-' + column.timestamp"
								>
									<div class="card d-flex justify-content-between" style="min-height:150px">
										<div class="items">
											<draggable
												:emptyInsertThreshold="200"
												:list="column.items"
												group="submenuItems"
												v-bind="dragOptions"
												handle=".move-item"
												data-source="juju"
											>
												<transition-group type="transition" :name="'flip-list'">
													<div v-for="(subItem, sIndex) in column.items" :key="'subitem-' + subItem.timestamp" class="megamenu-subitem">
														<button class="btn btn-secondary subitem-move">
															<font-awesome-icon class="move-item" :icon="['fas', 'arrows-alt']" />
														</button>
														<button @click="addMeagaMenuItem(subItem, subItem.children, 'children')" class="btn btn-secondary subitem-add">
															+
														</button>
														<button @click="removeMegaMenuSubItem(sIndex, column.items)" class="btn btn-danger subitem-remove">
															X
														</button>
														<button @click="setMegaMenuItemForSettings(subItem)" class="btn btn-secondary subitem-setting">
															<font-awesome-icon :icon="['fas', 'wrench']" />
														</button>
														<div class="subitem-heading">
															<div class="subitem-title">
																{{ subItem.label }}
															</div>
														</div>
														<div v-if="subItem.children" class="submenu-cilds rounded-1">
															<draggable :emptyInsertThreshold="200" :list="subItem.children" handle=".move-item">
																<div
																	v-for="(subItemChild, cIndex) in subItem.children"
																	:key="'subItemChild-' + subItemChild.timestamp"
																	class="megamenu-subitem-childs"
																>
																	<div class="subitem-child-container card p-1 m-1 mb-1">
																		<div class="subitem-child-title">
																			<font-awesome-icon class="move-item me-1" :icon="['fas', 'bars']" />{{ subItemChild.label }}
																		</div>
																		<div class="subitem-child-actions">
																			<a role="button" @click="setMegaMenuItemForSettings(subItemChild)" class="text-secondary">
																				<font-awesome-icon :icon="['fas', 'wrench']" />
																			</a>
																			<a role="button" @click="removeMegaMenuSubItem(cIndex, subItem.children)" class="text-danger ms-1">
																				<font-awesome-icon :icon="['fas', 'trash-alt']" />
																			</a>
																		</div>
																	</div>
																</div>
															</draggable>
														</div>
														<div v-else class="submenu-cilds mt-2 p-1 rounded-1 d-flex justify-content-center align-items-center">
															<span class="text-secondary text-center"
																>This menu has no child items
																<strong><a role="button" @click="addMeagaMenuItem(subItem, subItem.children, 'children')">add item</a></strong>
															</span>
														</div>
													</div>
												</transition-group>
											</draggable>
										</div>

										<button @click="addMeagaMenuItem(column, column.items, 'items')" class="btn btn-primary btn-sm w-100">
											+ Add Item
										</button>
									</div>
								</div>
							</div>
						</div>
						<!-- Item Settings Section -->
						<div class="card mt-3 p-3 item-settings" v-if="megaMenuItemSettings">
							<div class="d-flex justify-content-between align-items-center">
								<h6>Settings for: {{ megaMenuItemSettings.label }}</h6>
								<h6>
									<span class="badge badge-primary text-uppercase">{{ megaMenuItemSettings.type }}</span>
								</h6>
							</div>

							<div class="megamenu-sub-item-settings">
								<div class="menu-item-settings">
									<div v-if="megaMenuItemSettings.type == 'image'">
										<div class="form-group mb-2">
											<label for="">Image URL</label>
											<div class="d-flex">
												<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="megaMenuItemSettings.imageUrl" />
												<button @click="openMediaLibraryForImageSettings()" title="Open Media Library" class="btn btn-primary btn-sm ms-1">
													<font-awesome-icon :icon="['fas', 'images']" />
												</button>
											</div>
										</div>
										<div class="form-group mb-2" v-if="megaMenuItemSettings.imageUrl">
											<img :src="megaMenuItemSettings.imageUrl" style="max-width: 100%; max-height: 100px; object-fit: contain;" class="border rounded" />
										</div>
										<div class="form-group mb-2">
											<label for="">Alt Text</label>
											<input type="text" class="form-control form-control-sm" v-model="megaMenuItemSettings.altText" />
										</div>
										<div class="form-check mb-2">
											<input class="form-check-input" type="checkbox" v-model="megaMenuItemSettings.wrapInLink" :id="'imageSettingsWrapInLink-' + megaMenuItemSettings.timestamp" />
											<label class="form-check-label" :for="'imageSettingsWrapInLink-' + megaMenuItemSettings.timestamp">
												Wrap in Link
											</label>
										</div>
										<div v-if="megaMenuItemSettings.wrapInLink">
											<div class="form-group mb-2">
												<label for="">Link URL</label>
												<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="megaMenuItemSettings.linkUrl" />
											</div>
											<div class="form-check mb-2">
												<input class="form-check-input" type="checkbox" v-model="megaMenuItemSettings.newTab" :id="'imageSettingsNewTab-' + megaMenuItemSettings.timestamp" />
												<label class="form-check-label" :for="'imageSettingsNewTab-' + megaMenuItemSettings.timestamp">
													Open in New Tab
												</label>
											</div>
										</div>
									</div>
									<div class="form-group mb-2" v-else-if="megaMenuItemSettings.type == 'custom'">
										<label for="">URL</label>
										<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="megaMenuItemSettings.url" />
									</div>
									<div v-else class="form-group mb-2">Original: {{ megaMenuItemSettings.title }}</div>

									<div class="form-group mb-2">
										<label for="">Navigation Label</label>
										<input type="text" class="form-control form-control-sm" v-model="megaMenuItemSettings.label" />
									</div>

									<div class="form-group mb-2">
										<label for="">Description</label>
										<input type="text" class="form-control form-control-sm" v-model="megaMenuItemSettings.description" />
									</div>

									<div class="form-group mb-2">
										<label for="">Icon </label>

										<input type="text" class="form-control form-control-sm" v-model="megaMenuItemSettings.icon" />
										<small
											>Example: <code>fa-solid fa-house</code>
											<a class="ms-2" target="_blank" href="https://fontawesome.com/search?o=r&m=free">available icons</a></small
										>
									</div>

									<div class="form-group mb-2">
										<label for="">Class </label>
										<input type="text" class="form-control form-control-sm" v-model="megaMenuItemSettings.class" />
										<small><code>Additional class for menu item</code></small>
									</div>

									<div class="row mb-2">
										<div class="col-md-4">
											<!-- Disable link -->
											<div class="form-check mb-2">
												<input
													class="form-check-input"
													type="checkbox"
													v-model="megaMenuItemSettings.disabled"
													:id="'disableLink-' + megaMenuItemSettings.timestamp"
												/>
												<label class="form-check-label" :for="'disableLink-' + megaMenuItemSettings.timestamp">
													Disable Link
												</label>
											</div>
										</div>
										<div class="col-md-4">
											<!-- New tab -->
											<div class="form-check mb-2">
												<input
													class="form-check-input"
													type="checkbox"
													v-model="megaMenuItemSettings.newTab"
													:id="'newTab-' + megaMenuItemSettings.timestamp"
												/>
												<label class="form-check-label" :for="'newTab-' + megaMenuItemSettings.timestamp">
													New Tab
												</label>
											</div>
										</div>
										<div class="col-md-4">
											<!-- No label -->
											<div class="form-check mb-2">
												<input
													class="form-check-input"
													type="checkbox"
													v-model="megaMenuItemSettings.noLabel"
													:id="'noLabel-' + megaMenuItemSettings.timestamp"
												/>
												<label class="form-check-label" :for="'noLabel-' + megaMenuItemSettings.timestamp">
													Disable Label
												</label>
											</div>
										</div>
									</div>

									<div class="row mb-2">
										<div class="col-md-6">
											<!-- Rel -->
											<div class="form-group">
												<label :for="'rel' + megaMenuItemSettings.timestamp">Rel</label>
												<select class="form-select">
													<option value="">none</option>
													<option v-for="(relItem, i) in relOptions" :value="relItem" :key="i + 'rel'">{{ relItem }}</option>
												</select>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-md-12 d-flex justify-content-between align-items-center">
											<button class="btn btn-secondary mb-3 mt-2" @click="megaMenuItemSettings = null">
												Close
											</button>
											<button class="btn btn-primary mb-3 mt-2" @click="megaMenuItemSettings = null">
												Apply
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
						<!--Add New Item Section -->
						<div v-if="megamenuAddSubmenuFlag" class="megamenu-adding-items mt-3 p-3 card">
							<h5>Select menu item from groups</h5>
							<small>Note that is always better to use predefined items if you need some autogenerated pages</small>
							<div class="accordion" id="accordionExample">
								<!-- Pages for menu -->
								<div class="accordion-item pages-item">
									<h2 class="accordion-header" id="headingPages">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapsePages"
											aria-expanded="false"
											aria-controls="collapsePages"
										>
											Pages
										</button>
									</h2>
									<div id="collapsePages" class="accordion-collapse collapse" aria-labelledby="headingPages" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white" style="max-height:205px;overflow:auto">
											<div class="form-group mb-2">
												<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchPageItems()" v-model="pagesSearch" />
											</div>
											<div class="available-pages">
												<template v-for="page in availableMenuItems.pages">
													<div class="d-flex justify-content-between align-items-center mb-2" :key="page.id + '-page-menu-item'" v-if="!page.hidden">
														<span>{{ page.title }}</span>
														<button @click="addItemToMegaMenu(page, 'page')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- News for menu -->
								<div class="accordion-item news-item">
									<h2 class="accordion-header" id="headingNews">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseNews"
											aria-expanded="false"
											aria-controls="collapseNews"
										>
											News & Notices
										</button>
									</h2>
									<div id="collapseNews" class="accordion-collapse collapse" aria-labelledby="headingNews" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white" style="max-height:205px;overflow:auto">
											<div class="form-group mb-2">
												<input type="text" class="form-control form-control-sm" placeholder="Search..." @input="searchNewsItems()" v-model="newsSearch" />
											</div>
											<div class="available-news">
												<template v-for="newsItem in availableMenuItems.news">
													<div
														class="d-flex justify-content-between align-items-center mb-2"
														:key="newsItem.id + '-news-menu-item'"
														v-if="!newsItem.hidden"
													>
														<span>{{ newsItem.title }}</span>
														<button @click="addItemToMegaMenu(newsItem, 'news')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- Departments for menu -->
								<div class="accordion-item departments-item">
									<h2 class="accordion-header" id="headingDepartments">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseDepartments"
											aria-expanded="false"
											aria-controls="collapseDepartments"
										>
											Departments
										</button>
									</h2>
									<div id="collapseDepartments" class="accordion-collapse collapse" aria-labelledby="headingDepartments" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white" style="max-height:205px;overflow:auto">
											<div class="form-group mb-2">
												<input
													type="text"
													class="form-control form-control-sm"
													placeholder="Search..."
													@input="searchDepartmentsItems()"
													v-model="departmentsSearch"
												/>
											</div>
											<div class="available-departments">
												<template v-for="departmentItem in availableMenuItems.departments">
													<div
														class="d-flex justify-content-between align-items-center mb-2"
														:key="departmentItem.id + '-departments-menu-item'"
														v-if="!departmentItem.hidden"
													>
														<span>
															{{ departmentItem.title }}
															<small v-if="departmentItem.type && departmentItem.type !== 'department'" class="text-muted ms-1">/{{ departmentItem.prefix }}</small>
														</span>
														<button @click="addItemToMegaMenu(departmentItem, 'department')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- Predefined for menu -->
								<div class="accordion-item predefined-item">
									<h2 class="accordion-header" id="headingPredefined">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapsePredefined"
											aria-expanded="false"
											aria-controls="collapsePredefined"
										>
											Predefined
										</button>
									</h2>
									<div id="collapsePredefined" class="accordion-collapse collapse" aria-labelledby="headingPredefined" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white">
											<div class="available-predefined">
												<template v-for="(pItem, index) in availableMenuItems.predefined">
													<div :key="index + '-page-menu-item'" class="d-flex justify-content-between align-items-center mb-2">
														<span>{{ pItem.title }}</span>
														<button @click="addItemToMegaMenu(pItem, 'predefined')" class="btn btn-sm btn-outline-primary">
															Add
														</button>
													</div>
												</template>
											</div>
										</div>
									</div>
								</div>
								<!-- Custom URLs for menu -->
								<div class="accordion-item custom-item">
									<h2 class="accordion-header" id="headingCustom">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseCustom"
											aria-expanded="false"
											aria-controls="collapseCustom"
										>
											Custom URL
										</button>
									</h2>
									<div id="collapseCustom" class="accordion-collapse collapse" aria-labelledby="headingCustom" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white">
											<div class="form-group mb-2">
												<label for="">URL</label>
												<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="customUrl.url" />
											</div>
											<div class="form-group mb-2">
												<label for="">Navigation Label</label>
												<input type="text" class="form-control form-control-sm" v-model="customUrl.label" />
											</div>
											<button
												:disabled="customUrl.url == '' || customUrl.label == ''"
												class="btn btn-primary btn-sm mb-3"
												@click="addItemToMegaMenu(customUrl, 'custom')"
											>
												Add
											</button>
										</div>
									</div>
								</div>
								<!-- Image for menu -->
								<div class="accordion-item image-item">
									<h2 class="accordion-header" id="headingImage">
										<button
											class="accordion-button collapsed p-2"
											type="button"
											data-bs-toggle="collapse"
											data-bs-target="#collapseImage"
											aria-expanded="false"
											aria-controls="collapseImage"
										>
											Image
										</button>
									</h2>
									<div id="collapseImage" class="accordion-collapse collapse" aria-labelledby="headingImage" data-bs-parent="#accordionExample">
										<div class="accordion-body bg-white">
											<div class="form-group mb-2">
												<label for="">Image URL</label>
												<div class="d-flex">
													<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="imageItem.imageUrl" />
													<button @click="openMediaLibraryForImage()" title="Open Media Library" class="btn btn-primary btn-sm ms-1">
														<font-awesome-icon :icon="['fas', 'images']" />
													</button>
												</div>
											</div>
											<div class="form-group mb-2" v-if="imageItem.imageUrl">
												<img :src="imageItem.imageUrl" style="max-width: 100%; max-height: 100px; object-fit: contain;" class="border rounded" />
											</div>
											<div class="form-group mb-2">
												<label for="">Alt Text</label>
												<input type="text" class="form-control form-control-sm" v-model="imageItem.altText" />
											</div>
											<div class="form-check mb-2">
												<input class="form-check-input" type="checkbox" v-model="imageItem.wrapInLink" id="imageWrapInLink" />
												<label class="form-check-label" for="imageWrapInLink">
													Wrap in Link
												</label>
											</div>
											<div v-if="imageItem.wrapInLink">
												<div class="form-group mb-2">
													<label for="">Link URL</label>
													<input type="text" class="form-control form-control-sm" placeholder="https://" v-model="imageItem.linkUrl" />
												</div>
												<div class="form-check mb-2">
													<input class="form-check-input" type="checkbox" v-model="imageItem.newTab" id="imageNewTab" />
													<label class="form-check-label" for="imageNewTab">
														Open in New Tab
													</label>
												</div>
											</div>
											<div class="form-group mb-2">
												<label for="">CSS Class</label>
												<input type="text" class="form-control form-control-sm" placeholder="custom-class" v-model="imageItem.class" />
											</div>
											<button
												:disabled="imageItem.imageUrl == ''"
												class="btn btn-primary btn-sm mb-3"
												@click="addItemToMegaMenu(imageItem, 'image')"
											>
												Add
											</button>
										</div>
									</div>
								</div>
							</div>
							<button class="btn btn-secondary btn-sm mt-3" @click="megamenuAddSubmenuFlag = false">
								close
							</button>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="ju-btn ju-btn--muted" data-bs-dismiss="modal">Close</button>
						<button type="button" class="ju-btn ju-btn--primary" data-bs-dismiss="modal">Apply</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import 'he-tree-vue/dist/he-tree-vue.css'
import { Tree, Draggable, Fold } from 'he-tree-vue'
import { isArray } from 'lodash'
import draggable from 'vuedraggable'
import { Modal } from 'bootstrap'
import MediaLibraryModal from '../../../components/juniper/media/MediaLibraryModal.vue'
//import restApi from '../../../api/index'

export default {
	name: 'ClientMenus',
	data() {
		return {
			loading: false,
			updateLoading: false,
			client: {},
			menus: [],
			customUrl: {
				url: '',
				label: '',
				type: 'custom',
				icon: '',
				class: '',
				disabled: false,
				noFollow: false,
				newTab: false,
				noLabel: false,
				rel: 'none' },
			imageItem: {
				type: 'image',
				imageUrl: '',
				altText: '',
				wrapInLink: false,
				linkUrl: '',
				newTab: false,
				class: '' },
			selectedMenuFromList: '',
			newMenu: {
				client_id: this.$route.params.id,
				type: 'default',
				name: '',
				display_location: [],
				items: [],
				html: '',
				menu_width: 'full' },
			selectedMenu: null,
			displayLocations: [
				{
					name: 'Primary',
					code: 'M-Primary' },
				{
					name: 'Hero',
					code: 'M-Hero' },
			],
			availableMenuItems: [],
			pagesSearch: '',
			newsSearch: '',
			departmentsSearch: '',
			relOptions: ['alternate', 'nofollow', 'noopener', 'noreferrer'],
			selectedMenuItems: [],
			allMenuItemsSelected: false,
			replaceSectionShow: false,
			settingsSectionShow: null,
			itemToReplace: null,
			replacingItem: null,
			replacesSearch: '',
			modelMegaMenu: null,
			megaMenuSettingsObject: null,
			megamenuAddSubmenuFlag: false,
			megaMenuArray: [],
			megaMenuItemSettings: null,
			megamenuAddMainItemFlag: false,
			mediaLibraryContext: 'newImage', // 'newImage' or 'settingsImage'
		}
	},
	components: { Tree: Tree.mixPlugins([Draggable, Fold]), draggable, MediaLibraryModal },
	computed: {
		// Department types (unified dashboard): at least one board/commission/committee
		// is on the website, so the typed archive links are worth offering
		hasTypedDepartments() {
			return (this.availableMenuItems?.departments || []).some(d => d.type && d.type !== 'department')
		},
		replacingItems() {
			let replacing = []

			this.availableMenuItems.pages.forEach(item => {
				let obj = {
					url: item.slug,
					title: item.title,
					type: 'page' }
				replacing.push(obj)
			})

			this.availableMenuItems.news.forEach(item => {
				let obj = {
					url: item.slug,
					title: item.title,
					type: 'news' }
				replacing.push(obj)
			})

			this.availableMenuItems.departments.forEach(item => {
				let obj = {
					url: item.slug,
					title: item.title,
					type: 'departments' }
				replacing.push(obj)
			})

			this.availableMenuItems.predefined.forEach(item => {
				let obj = {
					url: item.url,
					title: item.title,
					type: item.type }
				replacing.push(obj)
			})
			return replacing
		},
		dragOptions() {
			return {
				animation: 200,
				group: 'description',
				disabled: false,
				ghostClass: 'ghost' }
		},
		...mapState(['authUser']),
		...mapGetters(['isAuth']) },
	mounted() {
		this.modelMegaMenu = new Modal(this.$refs.modalMegaMenu)
	},
	created() {
		this.getClient()
	},
	watch: {
		'selectedMenu.type'(newType, oldType) {
			console.log('Menu type changed from', oldType, 'to', newType)
			// Reset selected menu items when menu type changes
			// this.selectedMenuItems = []
			// this.allMenuItemsSelected = false


			if (oldType == 'mega-menu' && (newType == 'default' || newType == 'accordion-menu')) {
				// Reverting items from mega-menu to default menu
				this.selectedMenu.items.forEach(mainItem => {
					mainItem.children = []
					if (Array.isArray(mainItem.columns)) {
						// If there is items in columns move them in children
						mainItem.columns.forEach(column => {
							if (column.items) {
								column.items.forEach(citem => {
									mainItem.children.push(citem)
								})
							}
						})
					}
				})
			}
		} },
	methods: {
		// Replace menu item
		selectItemToReplace(item) {
			this.itemToReplace = item
			this.replacingItem = null
			this.replaceSectionShow = true
		},

		// Replacing item selected from list
		selectReplacingItem(item) {
			this.replacingItem = item
		},

		replaceMenuItem() {
			this.itemToReplace.title = this.replacingItem.title
			this.itemToReplace.url = this.replacingItem.url
			this.itemToReplace.type = this.replacingItem.type
			this.itemToReplace.label = this.replacingItem.title
			this.replacesSearch = ''
			this.replaceSectionShow = false
		},

		// Select all menu items
		selectAllMenuItems() {
			this.selectedMenuItems = []
			if (!this.allMenuItemsSelected) {
				this.selectedMenu.items.forEach(menuItem => {
					this.selectedMenuItems.push(menuItem.timestamp.toString())
					if (menuItem.children && menuItem.children.length > 0) {
						this.selectedMenuItems = this.selectMenuItemChildren(menuItem.children, this.selectedMenuItems)
					}
				})
			}
		},

		// Select children menu items
		selectMenuItemChildren(children, selectedItems) {
			let selectedChildItems = []
			children.forEach(child => {
				selectedChildItems.push(child.timestamp.toString())
				if (child.children && child.children.length > 0) {
					selectedChildItems = this.selectMenuItemChildren(child.children, selectedChildItems)
				}
			})
			return selectedChildItems.concat(selectedItems)
		},

		selectMenuItem() {
			this.allMenuItemsSelected = false
		},

		// Remove selected menu items
		deleteSelectedItems() {
			this.$swal
				.fire({
					title: 'Are you sure?',
					text: 'You wont be able to rewert this action',
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Remove',
					toast: false,
					timer: 0 })
				.then(result => {
					if (result.isConfirmed) {
						this.selectedMenuItems.forEach(item => {
							this.removeMenuItem(item)
						})
					}
				})
		},

		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.getMenus()
					this.getAvailableMenuItems()
					this.getMenusList()
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: error,
						timer: 6000 })
				})
		},

		updateMenuOrder() {
			const newList = [...this.selectedMenu.items].map((item, index) => {
				const newSort = index + 1
				// also add in a new property called has changed if you want to style them / send an api call
				item.hasChanged = item.order !== newSort
				if (item.hasChanged) {
					item.order = newSort
				}
				return item
			})
			this.selectedMenu.items = newList
		},

		// Add item to menu
		addItemToMenu(item, type) {
			let exist = this.checkInMenu(item, type)
			if (exist) {
				this.$swal
					.fire({
						title: 'Are you sure?',
						text: 'Same item already exist in this menu!',
						icon: 'warning',
						position: 'center',
						showCancelButton: true,
						showConfirmButton: true,
						confirmButtonColor: '#3085d6',
						cancelButtonColor: '#d33',
						confirmButtonText: 'Add',
						toast: false,
						timer: 0 })
					.then(result => {
						if (result.isConfirmed) {
							this.addToMenuItemsArray(item, type)
							this.megamenuAddMainItemFlag = false
						}
					})
			} else {
				this.addToMenuItemsArray(item, type)
				this.megamenuAddMainItemFlag = false
			}
		},

		// Add item to menu array
		addToMenuItemsArray(item, type) {
			if (type == 'custom') {
				item.timestamp = Date.now()
				this.selectedMenu.items.push(JSON.parse(JSON.stringify(item)))
				this.customUrl.url = ''
				this.customUrl.label = ''
				this.customUrl.class = ''
				this.customUrl.disabled = false
				this.customUrl.rel = ''
				this.customUrl.newTab = ''
				this.customUrl.columnsCount = 1
				this.customUrl.columns = [
					{
						name: 'column-1',
						items: [],
						timestamp: Date.now() },
				]
			} else if (type == 'page') {
				let pageItem = {
					type: 'page',
					id: item.id,
					url: item.slug,
					label: item.title,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now(),
					columnsCount: 1,
					columns: [
						{
							name: 'column-1',
							items: [],
							timestamp: Date.now() },
					] }
				this.selectedMenu.items.push(pageItem)
			} else if (type == 'news') {
				let newsItem = {
					type: 'news',
					id: item.id,
					url: item.slug,
					label: item.title,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now(),
					columnsCount: 1,
					columns: [
						{
							name: 'column-1',
							items: [],
							timestamp: Date.now() },
					] }
				this.selectedMenu.items.push(newsItem)
			} else if (type == 'predefined') {
				let predefinedItem = {
					type: 'predefined',
					url: item.url,
					label: item.label,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now(),
					columnsCount: 1,
					columns: [
						{
							name: 'column-1',
							items: [],
							timestamp: Date.now() },
					] }
				this.selectedMenu.items.push(predefinedItem)
			} else if (type == 'department') {
				let departmentItem = {
					type: 'department',
					id: item.id,
					url: item.slug,
					label: item.title,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now(),
					columnsCount: 1,
					columns: [
						{
							name: 'column-1',
							items: [],
							timestamp: Date.now() },
					] }
				this.selectedMenu.items.push(departmentItem)
			}
		},

		// Check is item in menu
		checkInMenu(item, type) {
			let exist = false
			this.selectedMenu.items.forEach(menuItem => {
				if ((type == 'custom' || type == 'predefined') && menuItem.type == type && menuItem.url == item.url) {
					exist = true
					return
				}
				if (type != 'custom' && type !== 'predefined' && menuItem.type == type && menuItem.id == item.id) {
					exist = true
					return
				}
			})
			return exist
		},

		getMenusList() {
			juniperApi
				.get('/themes/' + this.$route.params.id + '/menus')
				.then(({ data }) => {
					let menus = []
					data.forEach(item => {
						menus.push({ name: item, code: 'm-' + item, label: item.replace('menu-', '').toUpperCase() })
					})
					this.displayLocations = menus
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: error,
						timer: 6000 })
				})
		},

		getMenus() {
			this.loading = true
			juniperApi
				.get('/menus/' + this.$route.params.id)
				.then(({ data }) => {
					this.menus = data
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: error,
						timer: 6000 })
				})
		},

		getAvailableMenuItems() {
			this.loading = true
			juniperApi
				.get('/menus/' + this.$route.params.id + '/available-items')
				.then(({ data }) => {
					this.availableMenuItems = data
					this.availableMenuItems.predefined = [
						{
							type: 'predefined',
							label: 'Home',
							title: 'Home',
							url: '$[home]' },
						{
							type: 'predefined',
							label: 'News & Notices',
							title: 'News & Notices Archive',
							url: '$[news_prefix]' },
						{
							type: 'predefined',
							label: 'Posts Archive',
							title: 'Posts Archive',
							url: '$[posts_prefix]' },
						{
							type: 'predefined',
							label: 'Departments Archive',
							title: 'Departments Archive',
							url: '$[departments_prefix]' },
						// Department types (unified dashboard): typed archives; they resolve to
						// the departments prefix when the feature is off for the client
						...(this.hasTypedDepartments
							? [
									{ type: 'predefined', label: 'Boards Archive', title: 'Boards Archive', url: '$[boards_prefix]' },
									{ type: 'predefined', label: 'Commissions Archive', title: 'Commissions Archive', url: '$[commissions_prefix]' },
									{ type: 'predefined', label: 'Committees Archive', title: 'Committees Archive', url: '$[committees_prefix]' },
								]
							: []),
						{
							type: 'predefined',
							label: 'Events',
							title: 'Events',
							url: '$[events_prefix]' },
						{
							type: 'predefined',
							label: 'Meetings',
							title: 'Meetings',
							url: '$[meetings_prefix]' },
						{
							type: 'predefined',
							label: 'Subscribe',
							title: 'Subscribe',
							url: '$[subscriber_prefix]' },
						{
							type: 'predefined',
							label: 'Faqs',
							title: 'Faqs',
							url: '$[faqs_prefix]' },
					]
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: error,
						timer: 6000 })
				})
		},

		// Store new menu in database
		storeMenu() {
			this.updateLoading = true
			let data = {}
			data.name = this.selectedMenu.name
			data.type = this.selectedMenu.type
			data.menu_width = this.selectedMenu.menu_width
			data.items = this.selectedMenu.items
			data.html = this.selectedMenu.html
			data.display_location = this.selectedMenu.display_location
			data.client_id = this.$route.params.id
			juniperApi
				.post('/menus/' + this.$route.params.id, data)
				.then(({ data }) => {
					this.selectedMenu = data
					this.selectedMenu.items = JSON.parse(data.json_data)
					this.selectedMenu.display_location = JSON.parse(data.display_location)
					this.updateLoading = false
					//this.getMenus()
					this.$swal.fire({
						icon: 'success',
						title: 'Menu successfully added!',
						timer: 3000 })
				})
				.catch(error => {
					this.updateLoading = false
					this.$swal.fire({
						icon: 'error',
						title: error,
						timer: 6000 })
				})
		},

		// Update selected menu in database
		updateMenu() {
			this.updateLoading = true
			let data = {}
			data.id = this.selectedMenu.id
			data.type = this.selectedMenu.type
			data.menu_width = this.selectedMenu.menu_width
			data.name = this.selectedMenu.name
			data.items = this.selectedMenu.items
			data.html = this.selectedMenu.html
			data.display_location = this.selectedMenu.display_location
			data.client_id = this.$route.params.id
			juniperApi
				.put('/menus/' + this.$route.params.id + '/' + data.id, data)
				.then(() => {
					this.updateLoading = false
					this.getMenus()
					this.$swal.fire({
						icon: 'success',
						title: 'Menu successfully updated!',
						timer: 3000 })
				})
				.catch(error => {
					this.updateLoading = false
					this.$swal.fire({
						icon: 'error',
						title: error,
						timer: 6000 })
				})
		},

		// Delete menu
		deleteMenu() {
			this.loading = true

			this.$swal
				.fire({
					title: 'Are you sure you want to delete this menu?',
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Delete',
					toast: false,
					timer: 0 })
				.then(result => {
					if (result.isConfirmed) {
						juniperApi
							.delete('/menus/' + this.$route.params.id + '/' + this.selectedMenu.id)
							.then(() => {
								this.loading = false
								this.selectedMenu = null
								this.getMenus()
								this.$swal.fire({
									icon: 'success',
									title: 'Menu successfully deleted!',
									timer: 3000 })
							})
							.catch(error => {
								this.loading = false
								this.$swal.fire({
									icon: 'error',
									title: error,
									timer: 6000 })
							})
					}
				})
		},

		// Select menu
		// Start a fresh, unsaved menu (deep copy so each new menu is independent)
		createNewMenu() {
			this.selectedMenuFromList = ''
			this.selectedMenu = JSON.parse(JSON.stringify(this.newMenu))
		},

		selectMenu(menu) {
			if (menu === '') {
				this.selectedMenu = null
			} else {
				let selected = JSON.parse(JSON.stringify(this.menus[menu]))
				selected.items = JSON.parse(selected.json_data)
				this.selectedMenu = selected
				if (!Array.isArray(this.selectedMenu.display_location)) {
					this.selectedMenu.display_location = JSON.parse(this.selectedMenu.display_location)
				}
			}
		},

		// Remove menu item
		removeMenuItem(timestamp) {
			let test = this.selectedMenu.items
			this.selectedMenu.items = this.removeElement(test, timestamp)
		},

		// Recursive function for removing menu item
		removeElement(data, timestamp) {
			return data
				.filter(dt => {
					return dt.timestamp !== timestamp
				})
				.map(dt => {
					if (dt.children) {
						return {
							...dt,
							children: this.removeElement(dt.children, timestamp) }
					}
					return dt
				})
		},

		// Search page items
		searchPageItems() {
			this.availableMenuItems.pages.forEach(item => {
				if (item.title.toLocaleLowerCase().includes(this.pagesSearch.toLocaleLowerCase())) {
					item.hidden = false
				} else {
					item.hidden = true
				}
			})
		},

		// Search news items
		searchNewsItems() {
			this.availableMenuItems.news.forEach(item => {
				if (item.title.toLocaleLowerCase().includes(this.newsSearch.toLocaleLowerCase())) {
					item.hidden = false
				} else {
					item.hidden = true
				}
			})
		},

		// Search departments items
		searchDepartmentsItems() {
			this.availableMenuItems.departments.forEach(item => {
				if (item.title.toLocaleLowerCase().includes(this.departmentsSearch.toLocaleLowerCase())) {
					item.hidden = false
				} else {
					item.hidden = true
				}
			})
		},

		// Search replace items
		searchReplaceItems() {
			this.replacingItems.forEach(item => {
				if (item.title.toLocaleLowerCase().includes(this.replacesSearch.toLocaleLowerCase())) {
					item.hidden = false
				} else {
					item.hidden = true
				}
			})
		},

		// Print locations
		printLocations(locations) {
			let text = ''
			if (isArray(JSON.parse(locations))) {
				JSON.parse(locations).forEach(location => {
					if (text !== '') {
						text += ','
					}
					text += location.replace('m-menu-', '')
				})
			}
			return text
		},

		addItem(index) {
			let test = {
				url: '',
				label: 'Test',
				class: '',
				disabled: '',
				rel: '',
				newTab: '',
				timestamp: Date.now() }
			if (!this.selectedMenu.items[index].children) {
				this.selectedMenu.items[index].children = []
			}
			this.selectedMenu.items[index].children.push(JSON.parse(JSON.stringify(test)))
		},

		openMegaMenuSettings(node) {
			this.megaMenuSettingsObject = node
			this.modelMegaMenu.show()
		},

		// Confirm how many columns you need to have in submenu
		confrimColumnsCount() {
			// Items from children - Default menu
			let submenuChildrens = this.megaMenuSettingsObject.children ? this.megaMenuSettingsObject.children : []
			// When changing columns that have items, move all items in first
			if (Array.isArray(this.megaMenuSettingsObject.columns)) {
				// If there is items in columns move them in children
				this.megaMenuSettingsObject.columns.forEach(column => {
					column.items.forEach(citem => {
						submenuChildrens.push(citem)
					})
				})
			}
			// Empty all columns from megamenu
			this.megaMenuSettingsObject.columns = []
			// Prepare data for new columns
			let columnData = {
				name: '',
				items: [],
				timestamp: '' }
			let i = 1
			while (i < parseInt(this.megaMenuSettingsObject.columnsCount) + 1) {
				columnData.name = 'column-' + i
				columnData.timestamp = Date.now() + i
				this.megaMenuSettingsObject.columns.push(JSON.parse(JSON.stringify(columnData)))
				i++
			}

			this.megaMenuSettingsObject.columns[0].items = submenuChildrens
		},

		// Change menu type
		// changeMenuType() {
		// 	if (this.selectedMenu.type == 'default' || this.selectedMenu.type == 'accordion-menu') {
		// 		// Reverting items from mega-menu to default menu
		// 		this.selectedMenu.items.forEach(mainItem => {
		// 			mainItem.children = []
		// 			if (Array.isArray(mainItem.columns)) {
		// 				// If there is items in columns move them in children
		// 				mainItem.columns.forEach(column => {
		// 					if (column.items) {
		// 						column.items.forEach(citem => {
		// 							mainItem.children.push(citem)
		// 						})
		// 					}
		// 				})
		// 			}
		// 		})
		// 	}
		// },

		// Add item to Mega Menu array
		addToMegaMenuItemsArray(item, type) {
			if (type == 'custom') {
				item.timestamp = Date.now()
				this.megaMenuArray.push(JSON.parse(JSON.stringify(item)))
				this.customUrl.url = ''
				this.customUrl.label = ''
				this.customUrl.class = ''
				this.customUrl.disabled = false
				this.customUrl.rel = ''
				this.customUrl.newTab = ''
			} else if (type == 'image') {
				let imageMenuItem = {
					type: 'image',
					imageUrl: item.imageUrl,
					altText: item.altText,
					wrapInLink: item.wrapInLink,
					linkUrl: item.linkUrl,
					newTab: item.newTab,
					class: item.class,
					label: item.altText || 'Image',
					timestamp: Date.now() }
				this.megaMenuArray.push(imageMenuItem)
				this.imageItem.imageUrl = ''
				this.imageItem.altText = ''
				this.imageItem.wrapInLink = false
				this.imageItem.linkUrl = ''
				this.imageItem.newTab = false
				this.imageItem.class = ''
			} else if (type == 'page') {
				let pageItem = {
					type: 'page',
					id: item.id,
					url: item.slug,
					label: item.title,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now() }
				this.megaMenuArray.push(pageItem)
			} else if (type == 'news') {
				let newsItem = {
					type: 'news',
					id: item.id,
					url: item.slug,
					label: item.title,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now() }
				this.megaMenuArray.push(newsItem)
			} else if (type == 'predefined') {
				let predefinedItem = {
					type: 'predefined',
					url: item.url,
					label: item.label,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now() }
				this.megaMenuArray.push(predefinedItem)
			} else if (type == 'department') {
				let departmentItem = {
					type: 'department',
					id: item.id,
					url: item.slug,
					label: item.title,
					title: item.title,
					icon: '',
					class: '',
					disabled: false,
					rel: '',
					newTab: false,
					noLabel: false,
					timestamp: Date.now() }
				this.megaMenuArray.push(departmentItem)
			}
		},

		// Add item to Mega Menu
		addItemToMegaMenu(item, type) {
			this.addToMegaMenuItemsArray(item, type)
			this.megamenuAddSubmenuFlag = false
		},

		addMeagaMenuItem(parent, items, propString) {
			if (Array.isArray(items)) {
				this.megaMenuArray = items
			} else {
				parent[propString] = []
				this.megaMenuArray = parent[propString]
			}
			this.megamenuAddSubmenuFlag = true
		},

		// Remove MegaMenu SubItem
		removeMegaMenuSubItem(index, items) {
			items.splice(index, 1)
		},

		setMegaMenuItemForSettings(item) {
			this.megaMenuItemSettings = item
		},

		menuItemsMainStatistic(item) {
			if (Array.isArray(item.columns)) {
				return item.columns.length + ' column/s'
			} else {
				return 'Mega Menu not set'
			}
		},

		// Open media library for image selection
		openMediaLibraryForImage() {
			this.mediaLibraryContext = 'newImage'
			this.$refs.mediaLibraryModal.$mediaModal = new Modal(document.getElementById('mediaLibraryModal'))
			this.$refs.mediaLibraryModal.$mediaModal.show()
		},

		// Open media library for image settings
		openMediaLibraryForImageSettings() {
			this.mediaLibraryContext = 'settingsImage'
			this.$refs.mediaLibraryModal.$mediaModal = new Modal(document.getElementById('mediaLibraryModal'))
			this.$refs.mediaLibraryModal.$mediaModal.show()
		},

		// Handle media insert from media library
		handleMediaInsert(mediaUrl) {
			if (this.mediaLibraryContext === 'newImage') {
				this.imageItem.imageUrl = mediaUrl
			} else if (this.mediaLibraryContext === 'settingsImage') {
				this.megaMenuItemSettings.imageUrl = mediaUrl
			}
			this.$refs.mediaLibraryModal.$mediaModal.hide()
		} } }
</script>

<style lang="scss">
#clientDns {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

/* ─── Settings-language pass (scoped to the menus page) ──────────────────── */
#clientDns {
	/* Header action pill — matches Dashboard/Website quick actions */
	.hu-pill {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 7px 12px;
		border: 1px solid #e0e1e1;
		border-radius: 999px;
		background: #fff;
		color: #64748b;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
		cursor: pointer;
		transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

		svg { font-size: 12px; }

		&:hover:not(:disabled) {
			background: #f8f9fa;
			border-color: #d1d5db;
			color: #475569;
		}

		&--accent {
			background: #111827;
			border-color: #111827;
			color: #fff;

			&:hover:not(:disabled) {
				background: #000;
				border-color: #000;
				color: #fff;
			}
		}
	}

	/* Menu picker toolbar */
	.jm-toolbar {
		display: flex;
		align-items: center;
		gap: 10px;
		flex-wrap: wrap;
		border: 1px solid #e6e8ec;
		border-radius: 12px;
		padding: 12px 14px;
		background: transparent;
	}

	.jm-toolbar__label {
		font-size: 0.8rem;
		font-weight: 600;
		color: #6c757d;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.jm-toolbar__or {
		color: #8a909c;
		font-size: 13px;
	}

	.jm-select {
		min-width: 320px;
		max-width: 100%;
		font-size: 13px;
		padding: 6px 10px;
		border: 1px solid #e6e8ec;
		border-radius: 8px;
		color: #374151;
		background: #fff;
		outline: none;

		&:focus {
			border-color: #2b6cff;
			box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
		}
	}

	/* Menu-type panel + segmented control */
	.jm-panel {
		border: 1px solid #e6e8ec;
		border-radius: 12px;
		padding: 12px 14px;
		background: transparent;
	}

	.jm-panel__row {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
	}

	.jm-panel__label {
		font-size: 0.8rem;
		font-weight: 600;
		color: #6c757d;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.jm-seg {
		display: inline-flex;
		background: #f3f4f6;
		border-radius: 999px;
		padding: 4px;
		gap: 0;
	}

	.jm-seg__btn {
		border: none;
		background: none;
		padding: 5px 16px;
		font-size: 13px;
		font-weight: 600;
		color: #6b7280;
		border-radius: 999px;
		cursor: pointer;
		transition: all 0.15s ease;

		&:hover:not(.active) { color: #374151; }

		&.active {
			background: #fff;
			color: #111827;
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
		}
	}

	.jm-panel__hint {
		margin: 10px 0 0;
		font-size: 12px;
		color: #6b7280;

		&--warn { color: #92400e; }
	}

	/* Info note (replaces the loud bootstrap alert) */
	.jm-note {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 13px;
		color: #1e40af;
		background: #eff6ff;
		border: 1px solid #bfdbfe;
		border-radius: 10px;
		padding: 8px 12px;
	}

	/* Mega-menu canvas — light gray so the item rows separate/pop */
	.jm-mega {
		background: #f7f8fa;
	}

	.jm-mega .megamenu-main-items-container {
		background: #fff;
		padding: 7px 14px;
		margin-bottom: 8px !important;
		border: 1px solid #e6e8ec;
		border-radius: 10px;
		transition: border-color 0.12s ease, box-shadow 0.12s ease;

		&:hover {
			border-color: #d5d9e0;
			box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
		}

		&:last-child { margin-bottom: 0 !important; }
	}

	.jm-mega .megamenu-main-item {
		min-height: 34px;
	}

	/* Borderless "Delete N items" — gray by default, red on hover (table language) */
	.jm-del {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		padding: 0.35rem 0.6rem;
		font-size: 0.85rem;
		font-weight: 600;
		border: none;
		background: transparent;
		color: #8a909c;
		border-radius: 7px;
		cursor: pointer;
		transition: color 0.15s ease, background 0.15s ease;

		&:hover {
			color: #dc3545;
			background: #fdecee;
		}
	}

	/* Default builder canvas — light gray so item rows separate/pop */
	.jm-default {
		background: #f7f8fa;

		/* Menu item rows stay white and pop off the gray canvas */
		#accordionMenu .accordion-item {
			background: #fff;
			border: 1px solid #e6e8ec;
			border-radius: 10px;
			overflow: hidden;
		}

		#accordionMenu .accordion-button {
			background: #fff;
			box-shadow: none;

			&:not(.collapsed) {
				color: #111827;
				background: #fff;
				box-shadow: none;
			}

			&:focus {
				box-shadow: none;
				border-color: transparent;
			}

			/* Smaller, softer collapse chevron */
			&::after {
				width: 0.8rem;
				height: 0.8rem;
				background-size: 0.8rem;
				opacity: 0.55;
			}
		}

		/* Fold/unfold toggle for items with children (replaces ➕/➖ emoji) */
		.jm-fold {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 22px;
			height: 22px;
			margin: 0 2px 0 4px;
			padding: 0;
			border: none;
			background: transparent;
			color: #8a909c;
			border-radius: 6px;
			font-size: 11px;
			cursor: pointer;
			transition: background 0.15s ease, color 0.15s ease;

			&:hover {
				background: #f1f3f5;
				color: #111827;
			}

			svg { transition: transform 0.15s ease; }

			&--open svg { transform: rotate(90deg); }
		}
	}

	/* Mega-menu row action buttons (config / Mega Menu) → flat */
	.action-button {
		display: inline-flex;
		align-items: center;
		gap: 0.35rem;
		padding: 0.25rem 0.7rem;
		font-size: 0.78rem;
		font-weight: 600;
		border: 1px solid #e6e8ec;
		border-radius: 7px;
		background: transparent;
		color: #374151;
		cursor: pointer;
		transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

		&:hover {
			background: #f3f5f9;
			color: #111827;
		}

		&--accent {
			border-color: #111827;
			color: #111827;
		}
	}

	/* Per-row column stat */
	.jm-stat {
		font-family: 'SF Mono', Menlo, Monaco, Consolas, 'Courier New', monospace;
		font-size: 12px;
		color: #8a909c;

		&--unset { color: #b45309; }
	}

	/* Available-items panel (left column of the default builder) */
	.jm-items-panel {
		border: 1px solid #e6e8ec;
		border-radius: 12px;
		overflow: hidden;
		background: #fff;

		.accordion-item {
			border: none;
			border-bottom: 1px solid #f1f3f5;
			border-radius: 0;
			background: transparent;

			&:last-child { border-bottom: none; }
		}

		.accordion-button {
			padding: 12px 16px;
			font-size: 13px;
			font-weight: 600;
			color: #374151;
			background: #fff;
			box-shadow: none;

			&:not(.collapsed) {
				color: #111827;
				background: #f9fafb;
				box-shadow: none;
			}

			&:focus {
				box-shadow: none;
				border-color: transparent;
			}

			&::after {
				width: 0.9rem;
				height: 0.9rem;
				background-size: 0.9rem;
			}
		}

		.accordion-body { padding: 10px 12px; }

		/* Item rows */
		.available-pages > div,
		.available-news > div,
		.available-departments > div,
		.available-predefined > div {
			margin-bottom: 2px !important;
			padding: 6px 8px;
			border-radius: 7px;
			gap: 10px;
			transition: background 0.12s ease;

			&:hover { background: #f3f5f9; }

			> span {
				font-size: 13px;
				color: #374151;
				line-height: 1.3;
				min-width: 0;
			}
		}

		/* Add button — compact pill, never wraps */
		.btn-outline-primary {
			white-space: nowrap;
			flex-shrink: 0;
			padding: 3px 12px;
			font-size: 12px;
			border-radius: 999px;
		}
	}

	/* Flat action buttons (settings language) */
	.ju-btn {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		padding: 0.4rem 0.9rem;
		font-size: 0.85rem;
		font-weight: 600;
		border-radius: 8px;
		border: 1px solid #e6e8ec;
		background: transparent;
		color: #111827;
		cursor: pointer;
		transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

		&:hover { background: #f3f5f9; }

		&--muted { color: #6b7280; }
		&--primary { border-color: #111827; }
		&--danger {
			color: #dc3545;
			border-color: #f1c2c7;

			&:hover { background: #fdecee; }
		}
	}

	/* Card containers → settings border/radius */
	.card {
		border: 1px solid #e6e8ec;
		border-radius: 12px;
		box-shadow: none;
	}

	/* Inputs / selects → settings language */
	.form-control,
	.form-select {
		border: 1px solid #e6e8ec;
		border-radius: 8px;
		box-shadow: none;

		&:focus {
			border-color: #2b6cff;
			box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
		}
	}

	/* Neutral checkboxes (dark when checked, not blue) */
	.form-check-input:checked {
		background-color: #111827;
		border-color: #111827;
	}

	/* Small inline buttons (Add / Replace / Select / Remove) → flat */
	.btn-outline-primary,
	.btn-outline-info,
	.btn-outline-secondary {
		border-color: #e6e8ec;
		color: #111827;
		background: transparent;

		&:hover {
			background: #f3f5f9;
			border-color: #d1d5db;
			color: #111827;
		}
	}

	.btn-outline-danger {
		border-color: #f1c2c7;
		color: #dc3545;
		background: transparent;

		&:hover {
			background: #fdecee;
			border-color: #f1c2c7;
			color: #dc3545;
		}
	}
}

.fixed-header-logo {
	z-index: 0 !important;
}

.tree-node {
	border: none !important;
	padding: 0px !important;
	margin: 10px 0px !important;
}

.item-settings {
	position: absolute;
	top: 0;
	left: calc(50% - 180px);
}

.megamenu-adding-items {
	position: absolute;
	top: 0;
	left: calc(50% - 250px);
}

.megamenu-adding-items-main {
	position: absolute;
	bottom: 167px;
	left: 130px;
}

.megamenu-main-items-container {
	padding: 15px;
	.megamenu-main-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
}

.megamenu-subitem {
	background-color: #f7f7f7;
	border-radius: 5px;
	border: 1px solid #dfdfdf;
	padding: 10px;
	margin: 10px;
	position: relative;
	&:hover {
		.subitem-remove {
			display: flex;
		}
		.subitem-setting {
			display: flex;
		}
		.subitem-add {
			display: flex;
		}
		.subitem-move {
			display: flex;
		}
	}
	.subitem-remove {
		position: absolute;
		top: -10px;
		right: -4px;
		background-color: #ea5455;
		color: white;
		justify-content: center;
		display: none;
		align-items: center;
		border-radius: 50%;
		font-size: 10px;
		width: 24px !important;
		min-width: 24px !important;
		height: 24px;
		min-height: 24px;
	}
	.subitem-setting {
		position: absolute;
		top: -10px;
		right: 22px;
		background-color: var(--bs-secondary);
		color: white;
		justify-content: center;
		display: none;
		align-items: center;
		border-radius: 50%;
		font-size: 10px;
		width: 24px !important;
		min-width: 24px !important;
		height: 24px;
		min-height: 24px;
	}
	.subitem-add {
		position: absolute;
		top: -10px;
		left: 15px;
		background-color: var(--bs-primary);
		color: white;
		justify-content: center;
		display: none;
		align-items: center;
		border-radius: 50%;
		font-size: 10px;
		width: 24px !important;
		min-width: 24px !important;
		height: 24px;
		min-height: 24px;
	}
	.subitem-move {
		position: absolute;
		top: -10px;
		left: -10px;
		background-color: #f7f7f7;
		color: white;
		justify-content: center;
		display: none;
		align-items: center;
		border-radius: 50%;
		font-size: 10px;
		width: 24px !important;
		min-width: 24px !important;
		height: 24px;
		min-height: 24px;
	}
	.subitem-heading {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.subitem-actions {
		min-width: 65px;
	}
	.submenu-cilds {
		min-height: 60px;
		.subitem-child-container {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
			.subitem-child-title {
				display: flex;
				justify-content: flex-start;
				align-items: center;
				font-size: 12px;
			}
			.subitem-child-actions {
				display: flex;
				justify-content: flex-start;
				align-items: center;
				min-width: 30px;
				margin-right: 5px;
				a {
					display: none;
					font-size: 10px;
				}
			}
			&:hover {
				.subitem-child-actions a {
					display: inline-block;
				}
			}
		}
	}
}

.move-item {
	cursor: move;
	color: #ccc;
}
</style>
